import { Navigate } from "react-router";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@privacy/translations";
import { useQuery } from "@tanstack/react-query";
import { DynamicGrid } from "../dynamicGrid";
import { Drawer } from "../shared/Drawer";
import { TenantIcon2 } from "../shared/icons";
import { usePermission } from "../shared/permissions";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import {
  sharedProcessingByIdQueryOptions,
  sharedProcessingSharedOrganisationsQueryOptions,
} from "../api/sharedProcessings";
import { SharedProcessingDrawerHeader } from "./SharedProcessingDrawerHeader";
import { ShareSharedProcessingDialog } from "./ShareSharedProcessingDialog";

const NAME_COLUMN_NAME = "name";

export const SharedProcessingShareSubDrawer = () => {
  const sharedProcessingId = DynamicGrid.useSelectedRowId();
  const { hasPermission } = usePermission();
  const { paths } = usePrivacyNavigate();

  if (!hasPermission("nova-forms-api:enterprise-customers:registrations:manage")) {
    return <Navigate to={paths.sharedProcessingsInline(sharedProcessingId)} replace />;
  }

  return <SharedProcessingShareSubDrawerContent key={sharedProcessingId} sharedProcessingId={sharedProcessingId} />;
};

const SharedProcessingShareSubDrawerContent = ({ sharedProcessingId }: { sharedProcessingId: string }) => {
  const { t } = useTranslation();
  const { navigate } = usePrivacyNavigate();
  const sharedProcessingQuery = useQuery(sharedProcessingByIdQueryOptions(sharedProcessingId));
  const sharedOrganisationsQuery = useQuery(sharedProcessingSharedOrganisationsQueryOptions(sharedProcessingId));

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!sharedProcessingQuery.data) return <Drawer.Loading />;

        const organisations = sharedOrganisationsQuery.data;
        return (
          <Stack sx={{ height: "100%" }}>
            <SharedProcessingDrawerHeader
              activeTab="standard"
              statusIndicator="hidden"
              sharedProcessing={sharedProcessingQuery.data}
            />
            <Drawer.SubDrawerHeader
              onBackClick={() => navigate("..")}
              renderIcon={props => <TenantIcon2 {...props} />}
              title={t("SharedProcessing.Share.Header")}
            />
            {!organisations ? (
              <Drawer.Loading />
            ) : (
              <Drawer.SelectableGrid
                initialState={{ sorting: { sortModel: [{ field: NAME_COLUMN_NAME, sort: "asc" }] } }}
                getRowId={row => row.organisationId}
                rows={organisations}
                isRowLocked={row => row.isShared}
                noRowsOverlay={NoOrganisationsOverlay}
                selectionAction={{
                  label: t("SharedProcessing.Share.ShareSelected"),
                  renderDialog: ({ selectedIds, onClose, onSuccess }) => (
                    <ShareSharedProcessingDialog
                      sharedProcessingId={sharedProcessingId}
                      organisations={organisations.filter(organisation =>
                        selectedIds.includes(organisation.organisationId)
                      )}
                      onClose={onClose}
                      onShared={onSuccess}
                    />
                  ),
                }}
                columns={[
                  {
                    field: NAME_COLUMN_NAME,
                    flex: 1,
                    headerName: t("EnterpriseCustomer.Field.Name"),
                    valueGetter: (_, { organisationName }) => organisationName,
                    renderCell: ({ row }) => (
                      <Drawer.GridCell>
                        <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
                          <TenantIcon2 size="sm" />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "600", lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }}
                          >
                            {row.organisationName}
                          </Typography>
                        </Stack>
                      </Drawer.GridCell>
                    ),
                  },
                ]}
              />
            )}
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

const NoOrganisationsOverlay = () => <Drawer.GridNoRowsOverlay labelKey="SharedProcessing.Share.NoOrganisations" />;
